<script setup lang="ts">
type DataRequestData = App.DataRequests.Data.DataRequestData;
type EnumData = App.Base.Data.EnumData;
type FinancialAidData = App.Students.Data.FinancialAidData;
type PartialFinancialAidData = App.Students.Data.PartialFinancialAidData;

import { formatDate } from '@/utils/date';

import DataRequestDialog from '@/domains/data-request/components/DataRequestDialog.vue';
import FinancialAidDataRequestForm from '@/domains/data-request/components/student/financial-aid/FinancialAidDataRequestForm.vue';

const props = defineProps<{
  financialAid: FinancialAidData;
  statuses: EnumData[];
  studentCssProfiles: EnumData[];
  selectedForVerificationOptions: EnumData[];
  dataRequest?: DataRequestData;
}>();

const dataRequestAttributes = computed(
  () => props.dataRequest?.attributes as PartialFinancialAidData | undefined
);

const formUrl = computed(() => {
  if (props.dataRequest?.status.value === 'pending') {
    return route('student.data-requests.financial-aid.update', {
      dataRequest: props.dataRequest.id
    });
  }
  return route('student.data-requests.financial-aid.store');
});

const form = useForm({
  url: formUrl.value,
  method: 'POST',
  preserveState: false,
  fields: {
    financial_aid: {
      status: dataRequestAttributes.value?.status || props.financialAid.status,
      date_submitted: formatDate(
        dataRequestAttributes.value?.date_submitted ?? props.financialAid?.date_submitted,
        { format: 'yyyy-MM-dd' }
      ),
      css_profile:
        (dataRequestAttributes.value?.css_profile || props.financialAid?.css_profile) ?? null,
      selected_for_verification_step_one: (() => {
        const verificationValue =
          dataRequestAttributes.value?.selected_for_verification?.value ??
          props.financialAid.selected_for_verification.value;
        return verificationValue !== 'unknown'
          ? 'yes-selected'
          : verificationValue === 'unknown'
            ? 'unknown'
            : null;
      })(),
      selected_for_verification_step_two: (() => {
        const verificationValue =
          dataRequestAttributes.value?.selected_for_verification?.value ??
          props.financialAid.selected_for_verification.value;
        return verificationValue && verificationValue !== 'unknown' ? verificationValue : null;
      })(),
      pell_eligible: (() => {
        if (
          dataRequestAttributes.value &&
          Object.keys(dataRequestAttributes.value).includes('pell_eligible')
        ) {
          return dataRequestAttributes.value.pell_eligible as boolean | null;
        }
        return props.financialAid?.pell_eligible as boolean | null;
      })()
    },
    user_comment: props.dataRequest?.user_comment ?? ''
  },
  transform: (fields) => ({
    ...fields,
    requestable_id: props.financialAid.id,
    financial_aid: {
      status: fields.financial_aid.status?.value,
      date_submitted: fields.financial_aid.date_submitted,
      css_profile: fields.financial_aid.css_profile?.value,
      selected_for_verification:
        fields.financial_aid.selected_for_verification_step_one === 'yes-selected'
          ? fields.financial_aid.selected_for_verification_step_two
          : fields.financial_aid.selected_for_verification_step_one,
      pell_eligible: fields.financial_aid.pell_eligible
    }
  })
});
</script>

<template>
  <DataRequestDialog dataRequestType="Financial Aid" :form="form">
    <template #form>
      <FinancialAidDataRequestForm
        :dataRequest="dataRequest"
        :financialAid="financialAid"
        :form="form"
        :statuses="statuses"
        :studentCssProfiles="studentCssProfiles"
        :selectedForVerificationOptions="selectedForVerificationOptions"
      />
    </template>
  </DataRequestDialog>
</template>
